import { TransferFactoryWithChoiceContext } from '@digitalasset/transfer-instruction-api-ts-client';
import { useTransferFactoryWithContext } from '@hooks/mutations/allocation/useTransferFactoryWithContext';
import { useExerciseSubmission } from '@hooks/mutations/ledger/useSubmission';
import {
  Holding as TokenStandardHolding,
  InstrumentId,
} from '@daml.js/splice-api-token-holding/lib/Splice/Api/Token/HoldingV1';
import { ChoiceContext } from '@daml.js/splice-api-token-metadata/lib/Splice/Api/Token/MetadataV1';
import {
  TransferFactory,
  TransferFactory_Transfer,
  Transfer,
} from '@daml.js/splice-api-token-transfer-instruction/lib/Splice/Api/Token/TransferInstructionV1';
import { Holding } from '@daml.js/utility-registry-holding-v0/lib/Utility/Registry/Holding/V0/Holding';
import { ContractId } from '@daml/types';
import { toTokenStandardDisclosedContract } from '../../../utils/common';
import { ERR_INSTRUCT_XFER, SUC_INSTRUCT_XFER } from '../../../utils/strings';

interface Args {
  party: string;
  receiver: string;
  amount: string;
  source: string;
  instrumentId: string;
  holdingCids?: string[];
  reference?: string;
}
export const useOfferTransferInstruction = () => {
  const getTransferFactoryWithContext = useTransferFactoryWithContext();
  const { submit: createTransferInstruction } = useExerciseSubmission(
    TransferFactory.TransferFactory_Transfer,
    SUC_INSTRUCT_XFER,
    ERR_INSTRUCT_XFER,
  );

  const now = new Date();
  const instructTransfer = async (args: Args) => {
    const { reference, party, receiver, amount, source, instrumentId, holdingCids } = args;

    const transfer: Transfer = {
      sender: party,
      receiver,
      amount,
      instrumentId: {
        admin: source,
        id: instrumentId,
      } as InstrumentId,
      requestedAt: now.toISOString(),
      executeBefore: new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days later
      inputHoldingCids: holdingCids?.length
        ? holdingCids.map((holdingCid) =>
            Holding.toInterface(TokenStandardHolding, holdingCid as ContractId<Holding>),
          )
        : [],
      meta: { values: { 'splice.lfdecentralizedtrust.org/reason': reference || '' } },
    };

    const transferFactoryWithContext: TransferFactoryWithChoiceContext =
      await getTransferFactoryWithContext.mutateAsync({ registrar: source, transfer });

    const payload: TransferFactory_Transfer = {
      expectedAdmin: source,
      transfer,
      extraArgs: {
        context: transferFactoryWithContext.choiceContext.choiceContextData as ChoiceContext,
        meta: { values: {} },
      },
    };
    const disclosedContracts =
      transferFactoryWithContext.choiceContext.disclosedContracts.map(
        toTokenStandardDisclosedContract,
      ) || [];
    await createTransferInstruction({
      cid: transferFactoryWithContext.factoryId as ContractId<TransferFactory>,
      argument: payload,
      meta: {
        disclosedContracts,
      },
    });
  };
  return instructTransfer;
};
